import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { DeleteClient, GetClient, GetClients } from "./ClientStore";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { EditClient } from "./Edit";

export const GrantTypeMap = {
    authorization_code: "Authorization Code",
    implicit: "Implicit",
    client_credentials: "Client Credentials",
    hybrid: "Hybrid",
    "urn:ietf:params:oauth:grant-type:device_code": "Device Code",
};

const ClientsTable = ({ user, policies, organization, toggleCreateClientModal }) => {
    let hasPermission = (permission) => {
        if (user && policies) {
            if (user.claims.filter((c) => c.type == "http://schemas.microsoft.com/ws/2008/06/identity/claims/role" && c.value == "Admin").length > 0) {
                return true;
            } else {
                return policies.filter((policy) => policy.permission == permission).length > 0;
            }
        }
        return false;
    };

    const navigate = useNavigate();

    const [clients, setClients] = React.useState([]);
    const [showEditClientModal, setShowEditClientModal] = React.useState(false);
    const [currentClient, setCurrentClient] = React.useState(null);

    React.useEffect(() => {
        loadClients();
    }, [organization]);

    const loadClients = (client?) => {
        GetClients(organization.id).then((res) => {
            setClients(res.data);
            if (client) {
                setCurrentClient(client);
            }
        });
    };

    const [showDeleteClientModal, setShowDeleteClientModal] = React.useState(false);
    const [clientToDelete, setClientToDelete] = React.useState(null);

    const startDeleteClient = (id) => () => {
        setClientToDelete(id);
        setShowDeleteClientModal(true);
    };

    const deleteClient = (id) => {
        DeleteClient(id).then(() => {
            loadClients();
            setClientToDelete(null);
            setShowDeleteClientModal(false);
        });
    };

    const toggleEditClientModal = (id) => {
        if (showEditClientModal) {
            setShowEditClientModal(false);
            setCurrentClient(null);
        }

        if (!showEditClientModal && id !== null) {
            GetClient(id).then((response) => {
                setShowEditClientModal(true);
                setCurrentClient(response.data);
            });
        }
    };

    return (
        hasPermission("manageClients") && (
            <>
                <table className="table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Grant Type</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients &&
                            clients.map((client, i) => (
                                <tr key={i}>
                                    <td>
                                        {client.name}
                                        <br />
                                        <small>{client.id}</small>
                                    </td>
                                    <td>
                                        {client.grantTypes.filter((g) => g == "authorization_code").length > 0 && client.requirePKCE && (
                                            <>
                                                <FontAwesomeIcon icon={["fad", "lock"]} color="green"></FontAwesomeIcon>{" "}
                                            </>
                                        )}
                                        {client.grantTypes.filter((g) => g == "authorization_code").length > 0 && !client.requirePKCE && (
                                            <>
                                                <FontAwesomeIcon icon={["fad", "unlock"]} color="red"></FontAwesomeIcon>{" "}
                                            </>
                                        )}
                                        {client.grantTypes.map((g) => GrantTypeMap[g]).join(" | ")}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-outline-warning me-2"
                                            onClick={() => {
                                                navigate(`/organizations/clients/${client.id}/permissions`);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={["fad", "users-cog"]}></FontAwesomeIcon>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary me-2"
                                            onClick={() => {
                                                navigate(`/organizations/clients/${client.id}/secrets`);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={["fad", "key"]}></FontAwesomeIcon>
                                        </button>
                                        <button type="button" className="btn btn-outline-success me-2" onClick={() => toggleEditClientModal(client.id)}>
                                            <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>
                                        </button>
                                        <button type="button" className="btn btn-outline-danger" onClick={startDeleteClient(client.id)}>
                                            <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        <tr>
                            <td colSpan={2}>
                                <a href="#" onClick={toggleCreateClientModal}>
                                    Add new client
                                </a>
                            </td>
                            <td colSpan={2}>
                                <a
                                    href="#"
                                    onClick={() => {
                                        navigate(`/organizations/clients/manageExternal`);
                                    }}
                                >
                                    Manage external clients
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <Modal isOpen={showEditClientModal} toggle={() => toggleEditClientModal(null)} centered={true} size="xl">
                    <ModalHeader toggle={() => toggleEditClientModal(null)}>Edit Client</ModalHeader>
                    <ModalBody>{currentClient && <EditClient clientId={currentClient.id} organization={organization.id} updated={loadClients}></EditClient>}</ModalBody>
                    <ModalFooter>
                        <button type="button" className="btn btn-secondary" onClick={() => toggleEditClientModal(null)}>
                            Done
                        </button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={showDeleteClientModal} toggle={() => setShowDeleteClientModal(!showDeleteClientModal)} centered={true}>
                    <ModalHeader toggle={() => setShowDeleteClientModal(!showDeleteClientModal)}>Delete Client</ModalHeader>
                    <ModalBody>Are you sure you want to delete this client. This is irreversible and all associated secrets will be deleted.</ModalBody>
                    <ModalFooter>
                        <button type="button" className="btn btn-default" onClick={() => setShowDeleteClientModal(false)}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-danger" onClick={() => deleteClient(clientToDelete)}>
                            Delete
                        </button>
                    </ModalFooter>
                </Modal>
            </>
        )
    );
};

export default ClientsTable;
